import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';
import { 
  FileText, 
  Upload, 
  Sparkles, 
  Copy, 
  Check,
  Loader2,
  RefreshCw,
  Image as ImageIcon,
  X,
  Zap,
  Info,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';

export default function DescriptionGeneratorTab({ seoContext, onNavigateToSeo }) {
  // Polling interval for job status updates (milliseconds)
  const POLL_INTERVAL = 15000;
  const MAX_RETRIES = 4;

  // Estados de inputs
  const [productName, setProductName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  
  // Estados de generación
  const [generating, setGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [description, setDescription] = useState('');
  const [copied, setCopied] = useState(false);
  const pollTimerRef = useRef(null);

  // ══════════════════════════════════════════════════════════════════════════
  // SINCRONIZACIÓN CON CONTEXTO SEO
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (seoContext && seoContext.productName) {
      setProductName(seoContext.productName);
      setManualMode(false);
    }
  }, [seoContext]);

  // Verificar si hay datos SEO disponibles de la sesión actual
  const hasSeoData = seoContext && seoContext.productName && seoContext.keywords?.length > 0;
  
  // Tiempo desde el análisis SEO
  const seoAge = seoContext?.timestamp 
    ? Math.floor((new Date() - new Date(seoContext.timestamp)) / 60000) 
    : null;

  // Manejar imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen debe ser menor a 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, []);

  const pollJobStatus = useCallback(async (jobId, retryCount = 0) => {
    try {
      const { data } = await apiClient.get(`/api/descriptions/job-status/${jobId}`);

      if (data.status === 'pending' || data.status === 'processing') {
        setCurrentStep(data.step || 'Procesando...');
        pollTimerRef.current = setTimeout(() => pollJobStatus(jobId, 0), POLL_INTERVAL);
      } else if (data.status === 'completed') {
        setDescription(data.result?.description || '');
        setCurrentStep('');
        setGenerating(false);
        toast.success('Descripción generada exitosamente');
      } else if (data.status === 'error') {
        setCurrentStep('');
        setGenerating(false);
        toast.error(data.error || 'Error generando descripción');
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        pollTimerRef.current = setTimeout(() => pollJobStatus(jobId, retryCount + 1), 2000);
      } else {
        setCurrentStep('');
        setGenerating(false);
        toast.error('Error al consultar el estado de la generación');
      }
    }
  }, []);

  // Generar descripción
  const handleGenerate = async () => {
    if (!hasSeoData && !manualMode) {
      toast.error('Primero analiza un producto en el Optimizador SEO');
      return;
    }
    if (manualMode && !productName && !imageFile) {
      toast.error('Proporciona el nombre del producto o una imagen');
      return;
    }

    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setGenerating(true);
    setDescription('');
    setCurrentStep('Enviando solicitud...');

    try {
      const formData = new FormData();
      formData.append('product_name', productName);

      if (imageFile) formData.append('image', imageFile);

      if (hasSeoData && !manualMode) {
        formData.append('keywords_json', JSON.stringify(seoContext.keywords));
        formData.append('titles_json', JSON.stringify(seoContext.titles || []));
        formData.append('category', seoContext.category || '');
        formData.append('use_session_data', 'true');
      } else {
        formData.append('use_session_data', 'false');
      }

      const { data } = await apiClient.post('/api/descriptions/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setCurrentStep('En cola...');
      pollJobStatus(data.job_id);
    } catch (error) {
      console.error('Generate error:', error);
      setCurrentStep('');
      setGenerating(false);
      toast.error(error.response?.data?.detail || 'Error iniciando generación');
    }
  };

  // Copiar al portapapeles
  const copyToClipboard = () => {
    navigator.clipboard.writeText(description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Descripción copiada al portapapeles');
  };

  // Regenerar
  const handleRegenerate = () => {
    handleGenerate();
  };

  // Cambiar a modo manual
  const enableManualMode = () => {
    setManualMode(true);
    setProductName('');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-bold font-outfit text-foreground">Generador de Descripciones</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Genera descripciones <span className="text-blue-600 dark:text-blue-400 font-semibold">estructuradas y optimizadas</span> para 
          tus productos de Mercado Libre usando palabras clave SEO.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* CASO 1: NO HAY DATOS SEO - Mostrar aviso */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {!hasSeoData && !manualMode && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No hay datos del Optimizador SEO
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Para generar una descripción optimizada, primero analiza un producto en la pestaña 
                <span className="text-blue-600 dark:text-blue-400 font-medium"> Optimizador SEO</span>. 
                Esto nos dará las palabras clave y títulos para crear una descripción más efectiva.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onNavigateToSeo}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg flex items-center gap-2 transition-colors"
                  data-testid="go-to-seo-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Ir al Optimizador SEO
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={enableManualMode}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg flex items-center gap-2 transition-colors"
                  data-testid="manual-mode-btn"
                >
                  <FileText className="w-4 h-4" />
                  Ingresar datos manualmente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* CASO 2: HAY DATOS SEO - Panel de configuración */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {(hasSeoData || manualMode) && (
        <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Datos del Producto
          </h3>

          <div className="space-y-6">
            {/* Banner de datos SEO activos */}
            {hasSeoData && !manualMode && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">
                        Usando datos del Optimizador SEO
                      </span>
                      {seoAge !== null && (
                        <span className="text-xs text-muted-foreground/70">
                          hace {seoAge < 1 ? 'menos de 1' : seoAge} min
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Producto: <span className="text-emerald-700 dark:text-emerald-300 font-medium">{seoContext.productName}</span>
                      {' • '}{seoContext.keywords?.length || 0} palabras clave
                      {seoContext.titles?.length > 0 && ` • ${seoContext.titles.length} títulos`}
                    </p>
                    {seoContext.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {seoContext.keywords.slice(0, 8).map((kw, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded">
                            {kw}
                          </span>
                        ))}
                        {seoContext.keywords.length > 8 && (
                          <span className="px-2 py-0.5 text-xs bg-slate-700 text-muted-foreground rounded">
                            +{seoContext.keywords.length - 8} más
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={enableManualMode}
                  className="mt-3 text-xs text-muted-foreground/70 hover:text-muted-foreground underline"
                >
                  Cambiar a modo manual
                </button>
              </div>
            )}

            {/* Modo manual - Input de nombre */}
            {manualMode && (
              <>
                <div className="bg-muted border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Info className="w-4 h-4" />
                    <span>Modo manual activo - Los datos SEO no se usarán</span>
                  </div>
                  {hasSeoData && (
                    <button
                      onClick={() => {
                        setManualMode(false);
                        setProductName(seoContext.productName);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                    >
                      Volver a usar datos del Optimizador SEO
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Nombre del Producto *
                  </label>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Ej: Audífonos Bluetooth Sony WH-1000XM5"
                    className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder-muted-foreground/70 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    data-testid="description-product-name"
                  />
                </div>
              </>
            )}

            {/* Imagen del producto (opcional en ambos modos) */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Imagen del Producto <span className="text-muted-foreground/70">(opcional - enriquece la descripción)</span>
              </label>
              
              {imagePreview ? (
                <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-border">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={removeImage}
                    className="absolute top-1 right-1 p-1 bg-popover/80 rounded-full hover:bg-popover"
                  >
                    <X className="w-4 h-4 text-foreground" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-blue-500 hover:bg-muted transition-colors">
                  <ImageIcon className="w-8 h-8 text-muted-foreground/70 mb-2" />
                  <span className="text-sm text-muted-foreground/70">Subir imagen</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">
                La IA extraerá características adicionales de la imagen
              </p>
            </div>

            {/* Botón generar */}
            <button
              onClick={handleGenerate}
              disabled={generating || (manualMode && !productName && !imageFile)}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
              data-testid="generate-description-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {currentStep || 'Generando descripción...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generar Descripción
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* RESULTADO */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {description && (
        <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Descripción Generada
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleRegenerate}
                disabled={generating}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-muted text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                data-testid="regenerate-btn"
              >
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                Regenerar
              </button>
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                data-testid="copy-description-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Editor de texto */}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-[600px] px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm font-mono leading-relaxed focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            placeholder="La descripción aparecerá aquí..."
            data-testid="description-editor"
          />

          <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground/70">
            <Info className="w-4 h-4" />
            <span>Puedes editar la descripción antes de copiarla. Los separadores y encabezados son ajustables.</span>
          </div>
        </div>
      )}

      {/* Información del template (solo si hay datos y no hay descripción) */}
      {(hasSeoData || manualMode) && !description && !generating && (
        <div className="bg-popover/30 border border-border rounded-2xl p-6">
          <h4 className="text-sm font-semibold text-muted-foreground mb-3">La descripción incluirá:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            {[
              'Títulos SEO Secundarios',
              '¿Qué es el producto?',
              'Acerca de este producto',
              'Gancho Persuasivo',
              'Características',
              'Ventajas de comprarlo',
              'Se entrega con (Accesorios)',
              'Garantía',
              '30 días de prueba'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-muted-foreground">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                {item}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
